import { BuildTableData } from '../types';
import { FormattedMessage } from 'react-intl';
import { Link } from '@lifeomic/chroma-react/components/Link';
import { sharedMessages } from '../../sharedIntlLabels';
import { SpecificityIndicator } from '../../Indicators';
import { TableConfiguration } from '@lifeomic/chroma-react/components/TableModule';
import * as React from 'react';
import urls from '../../../../../src/util/urls';

// #region Value getters

export const getCKB = (value: any) => {
  const { jaxKnowledge } = value;

  if (!jaxKnowledge) {
    return null;
  }

  return <SpecificityIndicator variant="structural" match={jaxKnowledge} />;
};

export const getGene = (value: any) => {
  const { geneId1, geneId2 } = value;

  if (!geneId1 && !geneId2) {
    return null;
  }

  if (geneId1 && !geneId2) {
    return (
      <Link
        to={urls.ncbi.gene(geneId1)}
        onClick={e => e.stopPropagation()}
        newTab
      >
        {geneId1}
      </Link>
    );
  }

  if (!geneId1 && geneId2) {
    return (
      <Link
        to={urls.ncbi.gene(geneId2)}
        onClick={e => e.stopPropagation()}
        newTab
      >
        {geneId2}
      </Link>
    );
  }

  return (
    <FormattedMessage
      id="omicsExplorer.structuralVariantsContent.table.gene.bothGenes"
      defaultMessage="{gene1}-{gene2}"
      values={{
        gene1: (
          <Link
            to={urls.ncbi.gene(geneId1)}
            onClick={e => e.stopPropagation()}
            newTab
          >
            {geneId1}
          </Link>
        ),
        gene2: (
          <Link
            to={urls.ncbi.gene(geneId2)}
            onClick={e => e.stopPropagation()}
            newTab
          >
            {geneId2}
          </Link>
        ),
      }}
    />
  );
};

export const getGeneTextOnly = (value: any) => {
  const { geneId1, geneId2 } = value;

  if (!geneId1 && !geneId2) {
    return null;
  }

  if (geneId1 && !geneId2) {
    return geneId1;
  }

  if (!geneId1 && geneId2) {
    return geneId2;
  }

  return `${geneId1}-${geneId2}`;
};

export const getEffect = (value: any) => value?.type.toLowerCase();
export const getPosition = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  build, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  value,
}) => {
  const {
    chromosome1,
    chromosome2,
    endPosition1,
    endPosition2,
    startPosition1,
    startPosition2,
  } = value;

  // The string format is chr1:pos_start-pos_end | chr2:pos_start-pos_end.
  // If pos_start and pos_end are equal, just display chr:pos
  const pos1 = `${chromosome1 ? chromosome1 + ':' : null}${
    startPosition1 === endPosition1
      ? startPosition1
      : startPosition1 + '-' + endPosition1
  }`;
  const pos2 = `${chromosome2 ? chromosome2 + ':' : null}${
    startPosition2 === endPosition2
      ? startPosition2
      : startPosition2 + '-' + endPosition2
  }`;

  // If there's not at least a start and end position for chromosomes, do nothing.
  if (!((startPosition1 && endPosition1) || (startPosition2 && endPosition2))) {
    return null;
  }

  // If there is no build info or no chromosome, return a non-hyperlinked version of the string.
  if (!build || !chromosome1) {
    return (
      <FormattedMessage
        id="omicsExplorer.structuralVariantsContent.table.position.noBuildNoChrom1"
        defaultMessage="{pos1} | {pos2}"
        values={{ pos1, pos2 }}
      />
    );
  }

  // If there is build, chromosome, and position data, return a hyperlink
  return (
    <FormattedMessage
      id="omicsExplorer.structuralVariantsContent.table.position.value"
      defaultMessage="{pos1} | {pos2}"
      values={{
        pos1: (
          <Link
            to={urls.ucsc.genome(
              build,
              chromosome1,
              `${startPosition1}-${endPosition1}`,
            )}
            onClick={e => e.stopPropagation()}
            newTab
          >
            {pos1}
          </Link>
        ),
        pos2: (
          <Link
            to={urls.ucsc.genome(
              build,
              chromosome2,
              `${startPosition2}-${endPosition2}`,
            )}
            onClick={e => e.stopPropagation()}
            newTab
          >
            {pos2}
          </Link>
        ),
      }}
    />
  );
};
export const getLabReportedSignificance = (value: any) => value?.interpretation;
export const getInFrame = (value: any) => value?.inFrame;
// #endregion

export const buildTableData = ({
  variantSet,
  activeSequence,
  build,
  formatMessage,
  onSort,
  fetchPrependedColumns,
}: BuildTableData): Array<TableConfiguration> => [
  ...(fetchPrependedColumns?.(variantSet, activeSequence) ?? []),
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.jax),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['jaxKnowledge'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getCKB(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.gene),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['geneId1'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getGene(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.effect),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['type'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getEffect(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.position),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['chromosome1' || 25, 'startPosition1'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection, sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getPosition({ build, value }),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.labReportedSignificance),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['interpretation'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getLabReportedSignificance(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.inFrame),
    },
    cell: {
      content: value => getInFrame(value),
    },
  },
];
